import { Calendar, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { auditTimelineData, AuditEvent, AuditStatus } from '@/data/mockData';

export const AuditTimeline: React.FC = () => {
  const getStatusIcon = (status: AuditStatus) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="w-6 h-6 text-success-400" />;
      case 'In Progress':
        return <Clock className="w-6 h-6 text-primary-400 animate-pulse" />;
      case 'Scheduled':
        return <Calendar className="w-6 h-6 text-warning-400" />;
      case 'Cancelled':
        return <XCircle className="w-6 h-6 text-neutral-500" />;
    }
  };

  const getStatusColor = (status: AuditStatus) => {
    switch (status) {
      case 'Completed':
        return 'text-success-400 bg-success-500/20 border-success-500/30';
      case 'In Progress':
        return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
      case 'Scheduled':
        return 'text-warning-400 bg-warning-500/20 border-warning-500/30';
      case 'Cancelled':
        return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
    }
  };

  const upcomingAudits = auditTimelineData.filter(audit => 
    audit.status === 'Scheduled' || audit.status === 'In Progress'
  );

  const completedAudits = auditTimelineData.filter(audit => 
    audit.status === 'Completed'
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const TimelineItem: React.FC<{ audit: AuditEvent; isLast?: boolean }> = ({ audit, isLast }) => (
    <div className="flex gap-6 group">
      {/* Timeline Line */}
      <div className="flex flex-col items-center">
        <div className="bg-neutral-900 border-2 border-neutral-700 rounded-full p-2 group-hover:border-primary-500 transition-colors">
          {getStatusIcon(audit.status)}
        </div>
        {!isLast && (
          <div className="w-0.5 h-full bg-neutral-800 group-hover:bg-primary-500/30 transition-colors" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-8">
        <div 
          className="bg-neutral-950 rounded-lg border border-neutral-800 p-6 hover:border-primary-500/30 transition-all cursor-pointer"
          onClick={() => console.log('View audit:', audit.id)}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-sm text-primary-400">{audit.id}</span>
                <span className={`px-3 py-1 rounded text-xs font-medium border ${getStatusColor(audit.status)}`}>
                  {audit.status}
                </span>
              </div>
              <h3 className="text-white text-lg font-semibold group-hover:text-primary-400 transition-colors">
                {audit.title}
              </h3>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-neutral-500 text-xs mb-1">Date</p>
              <p className="text-neutral-300 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(audit.date)}
              </p>
            </div>
            
            <div>
              <p className="text-neutral-500 text-xs mb-1">Auditor</p>
              <p className="text-neutral-300 text-sm">{audit.auditor}</p>
            </div>

            <div className="md:col-span-2">
              <p className="text-neutral-500 text-xs mb-1">Scope</p>
              <p className="text-neutral-300 text-sm">{audit.scope}</p>
            </div>

            {audit.outcome && (
              <div className="md:col-span-2">
                <p className="text-neutral-500 text-xs mb-1">Outcome</p>
                <p className="text-neutral-300 text-sm font-medium">{audit.outcome}</p>
              </div>
            )}

            {audit.findings !== undefined && (
              <div>
                <p className="text-neutral-500 text-xs mb-1">Findings</p>
                <p className={`text-sm font-medium ${
                  audit.findings === 0 
                    ? 'text-success-400' 
                    : audit.findings <= 3 
                    ? 'text-warning-400' 
                    : 'text-danger-400'
                }`}>
                  {audit.findings} finding{audit.findings !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <ComingSoonWatermark 
        pageKey="audit-timeline"
        icon={Calendar}
        title="Audit Timeline Coming Soon"
        description="Comprehensive audit timeline with scheduling, findings tracking, remediation workflows, and historical audit records will be available soon."
      />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center">
            <Calendar className="w-8 h-8 text-primary-400 mr-3" />
            Audit Timeline
          </h1>
          <p className="text-neutral-400 mt-1">Track audit activities and compliance assessments</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Upcoming Audits</p>
              <p className="text-3xl font-bold text-warning-400 mt-2">
                {upcomingAudits.length}
              </p>
            </div>
            <div className="bg-warning-500/20 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-warning-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-400">
            Next 90 days
          </div>
        </div>

        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Completed (2024)</p>
              <p className="text-3xl font-bold text-success-400 mt-2">
                {completedAudits.length}
              </p>
            </div>
            <div className="bg-success-500/20 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-success-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-400">
            This year
          </div>
        </div>

        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Findings</p>
              <p className="text-3xl font-bold text-primary-400 mt-2">
                {completedAudits.reduce((sum, audit) => sum + (audit.findings || 0), 0)}
              </p>
            </div>
            <div className="bg-primary-500/20 p-3 rounded-lg">
              <AlertCircle className="w-6 h-6 text-primary-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-400">
            Across all audits
          </div>
        </div>
      </div>

      {/* Upcoming Audits Section */}
      {upcomingAudits.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <Clock className="w-6 h-6 text-warning-400 mr-2" />
              Upcoming Audits
            </h2>
          </div>

          <div className="space-y-0">
            {upcomingAudits.map((audit, index) => (
              <TimelineItem 
                key={audit.id} 
                audit={audit} 
                isLast={index === upcomingAudits.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed Audits Section */}
      {completedAudits.length > 0 && (
        <div className="space-y-4 mt-12">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <CheckCircle className="w-6 h-6 text-success-400 mr-2" />
              Completed Audits
            </h2>
          </div>

          <div className="space-y-0">
            {completedAudits.map((audit, index) => (
              <TimelineItem 
                key={audit.id} 
                audit={audit} 
                isLast={index === completedAudits.length - 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Calendar Integration Placeholder */}
      <div className="bg-gradient-to-r from-primary-500/10 to-cyan-500/10 rounded-lg border border-primary-500/30 p-8 text-center">
        <Calendar className="w-12 h-12 text-primary-400 mx-auto mb-4" />
        <h3 className="text-white text-lg font-semibold mb-2">Calendar Integration</h3>
        <p className="text-neutral-400 mb-4">
          Connect your calendar to automatically sync audit schedules and reminders
        </p>
        <button 
          onClick={() => console.log('Connect calendar')}
          className="px-6 py-2 bg-primary-500/20 text-primary-400 border border-primary-500/30 rounded-lg hover:bg-primary-500/30 transition-colors"
        >
          Connect Calendar
        </button>
      </div>
    </div>
  );
};
